import { CodeNameModel } from 'components/common/types';
import { DirectSalesCodeNameModel, DirectSalesLineObjective } from 'components/common/types/DirectSalesCampaign.types';
import useEditDirectSalesLine from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useEditDirectSalesLine';
import { DEFAULT_CRITERIA } from 'consts/targetCriteria';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateProductObjectives } from 'store/productManagement/reducer';
import { UseObjectiveSelection } from './useObjectiveSelection.types';

const isObjectiveValidAfterRemove = (objective: DirectSalesLineObjective): boolean => {
  const isFilter =
    objective.locations.length ||
    objective.formats.length ||
    objective.tags.length ||
    objective.frameList.listFiles.length ||
    objective.frameList.routeFrameCodes.length ||
    objective.frameList.mapRouteFrameCodes?.length ||
    objective.poi.length;

  const isTarget = !!objective.budget.value || !!objective.frames.value || !!objective.impressions.value;

  return !!isFilter && isTarget;
};

export const useObjectiveSelection = ({
  lineId,
  objectives,
  objective,
  onEditObjective,
  isProductObjective,
}: {
  lineId?: string;
  objectives: DirectSalesLineObjective[];
  objective: DirectSalesLineObjective;
  onEditObjective?: (newObjective: DirectSalesLineObjective) => void;
  isProductObjective?: boolean;
}): UseObjectiveSelection => {
  const dispatch = useDispatch();
  const editDealLine = useEditDirectSalesLine();

  const clearLocations = useCallback((): void => {
    const newObjective = { ...objective, locations: [] };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearFormats = useCallback((): void => {
    const newObjective = { ...objective, formats: [] };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearTags = useCallback((): void => {
    const newObjective = { ...objective, tags: [] };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearFrameList = useCallback((): void => {
    const newObjective = { ...objective, frameList: { routeFrameCodes: [], listFiles: [] } };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearPOI = useCallback((): void => {
    const newObjective = { ...objective, poi: [] };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.({ ...objective, poi: [] });
  }, [objective, onEditObjective]);

  const clearBudget = useCallback((): void => {
    const newObjective = { ...objective, budget: DEFAULT_CRITERIA };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearFrames = useCallback((): void => {
    const newObjective = { ...objective, frames: DEFAULT_CRITERIA };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const clearImpressions = useCallback((): void => {
    const newObjective = { ...objective, impressions: DEFAULT_CRITERIA };

    if (!isObjectiveValidAfterRemove(newObjective)) return;

    onEditObjective?.(newObjective);
  }, [objective, onEditObjective]);

  const onRemoveLocationChip = useCallback(
    (val?: string[], included?: boolean): void => {
      if (!val) {
        clearLocations();
        return;
      }

      if (!val.length) return;

      const objectivesCopy = structuredClone(objectives);

      const foundObjective = objectivesCopy.find((obj) => obj.objectiveGroupId === objective.objectiveGroupId);
      if (!foundObjective) return;

      const values = new Set(val);
      foundObjective.locations = foundObjective.locations.filter(
        (value: DirectSalesCodeNameModel & { category: string }) =>
          !values.has(value.name) || (included !== undefined && value.include !== included),
      );

      if (!isObjectiveValidAfterRemove(foundObjective)) return;

      if (isProductObjective) {
        dispatch(updateProductObjectives(objectivesCopy));
        return;
      }

      editDealLine({ lineId, directSalesData: { objectives: objectivesCopy } });
    },
    [lineId, objectives, objective, isProductObjective, editDealLine, clearLocations],
  );

  const onRemoveFormatChip = useCallback(
    (val?: string[], included?: boolean): void => {
      if (!val) {
        clearFormats();
        return;
      }

      if (!val.length) return;

      const objectivesCopy = structuredClone(objectives);

      const foundObjective = objectivesCopy.find((obj) => obj.objectiveGroupId === objective.objectiveGroupId);
      if (!foundObjective) return;

      const values = new Set(val);
      foundObjective.formats = foundObjective.formats.filter(
        (value: DirectSalesCodeNameModel & { category: string }) =>
          !values.has(value.name) || (included !== undefined && value.include !== included),
      );

      if (!isObjectiveValidAfterRemove(foundObjective)) return;

      if (isProductObjective) {
        dispatch(updateProductObjectives(objectivesCopy));
        return;
      }

      editDealLine({ lineId, directSalesData: { objectives: objectivesCopy } });
    },
    [lineId, objectives, objective, isProductObjective, editDealLine, clearFormats],
  );

  const onRemoveTagChip = useCallback(
    (val?: string[], included?: boolean): void => {
      if (!val) {
        clearTags();
        return;
      }

      if (!val.length) return;

      const objectivesCopy = structuredClone(objectives);

      const foundObjective = objectivesCopy.find((obj) => obj.objectiveGroupId === objective.objectiveGroupId);
      if (!foundObjective) return;

      const values = new Set(val);
      foundObjective.tags = foundObjective.tags.filter(
        (value: CodeNameModel & { category: string }) =>
          !values.has(value.name) || (included !== undefined && value.include !== included),
      );

      if (!isObjectiveValidAfterRemove(foundObjective)) return;

      if (isProductObjective) {
        dispatch(updateProductObjectives(objectivesCopy));
        return;
      }

      editDealLine({ lineId, directSalesData: { objectives: objectivesCopy } });
    },
    [lineId, objectives, objective, isProductObjective, editDealLine, clearTags],
  );

  const onRemoveFrameListChip = useCallback(
    (val?: string[], included?: boolean): void => {
      if (!val) {
        clearFrameList();
        return;
      }

      if (!val.length) return;

      const objectivesCopy = structuredClone(objectives);

      const foundObjective = objectivesCopy.find((obj) => obj.objectiveGroupId === objective.objectiveGroupId);
      if (!foundObjective) return;

      const values = new Set(val);

      if (values.has('Frames')) {
        foundObjective.frameList.routeFrameCodes = [];
      } else if (values.has('Map Frames')) {
        foundObjective.frameList.mapRouteFrameCodes = [];
      } else {
        foundObjective.frameList.listFiles = foundObjective.frameList.listFiles.filter(
          (value) => !values.has(value.name) || (included !== undefined && value.include !== included),
        );
      }

      if (!isObjectiveValidAfterRemove(foundObjective)) return;

      if (isProductObjective) {
        dispatch(updateProductObjectives(objectivesCopy));
        return;
      }

      editDealLine({ lineId, directSalesData: { objectives: objectivesCopy } });
    },
    [lineId, objectives, objective, isProductObjective, editDealLine, clearFrameList],
  );

  const onRemovePOIChip = useCallback(
    (val?: string[], included?: boolean): void => {
      if (!val) {
        clearPOI();
        return;
      }

      if (!val.length) return;

      const objectivesCopy = structuredClone(objectives);

      const foundObjective = objectivesCopy.find((obj) => obj.objectiveGroupId === objective.objectiveGroupId);
      if (!foundObjective) return;

      const values = new Set(val);
      foundObjective.poi = foundObjective.poi.filter(
        (value) =>
          (!values.has(value.name) && !values.has(value.code)) ||
          (included !== undefined && value.include !== included),
      );

      if (!isObjectiveValidAfterRemove(foundObjective)) return;

      if (isProductObjective) {
        dispatch(updateProductObjectives(objectivesCopy));
        return;
      }

      editDealLine({ lineId, directSalesData: { objectives: objectivesCopy } });
    },
    [lineId, objectives, objective, isProductObjective, editDealLine, clearPOI],
  );

  return {
    clearLocations,
    clearFormats,
    clearTags,
    clearFrameList,
    clearPOI,
    clearBudget,
    clearFrames,
    clearImpressions,
    onRemoveLocationChip,
    onRemoveFormatChip,
    onRemoveTagChip,
    onRemoveFrameListChip,
    onRemovePOIChip,
  };
};
